/**
 * Notification sound — Web Audio API synthesized sounds.
 *
 * Supports multiple sound presets, volume control, and sound preference
 * persistence via localStorage. No external audio files needed.
 */

import type { Notification } from "@/api/notifications";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { useTtsSettingsStore } from "@/stores/tts-settings-store";

const SOUND_ENABLED_KEY = "denker-sound-enabled";
const SOUND_VOLUME_KEY = "denker-sound-volume";
const SOUND_TYPE_KEY = "denker-sound-type";

export type SoundType = "chime" | "ping" | "bell";

export interface SoundPreset {
  type: SoundType;
}

export const SOUND_PRESETS: SoundPreset[] = [
  { type: "chime" },
  { type: "ping" },
  { type: "bell" },
];

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new AudioContext();
  }
  return audioCtx;
}

/* ---------- Preference getters/setters ---------- */

export function isSoundEnabled(): boolean {
  try {
    const stored = localStorage.getItem(SOUND_ENABLED_KEY);
    if (stored === "false") return false;
  } catch {
    // localStorage unavailable
  }
  return true; // default on
}

export function setSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(SOUND_ENABLED_KEY, String(enabled));
  } catch {
    // localStorage unavailable
  }
}

export function getSoundVolume(): number {
  try {
    const stored = localStorage.getItem(SOUND_VOLUME_KEY);
    if (stored !== null) {
      const val = parseFloat(stored);
      if (!isNaN(val) && val >= 0 && val <= 1) return val;
    }
  } catch {
    // localStorage unavailable
  }
  return 0.7; // default volume
}

export function setSoundVolume(volume: number): void {
  try {
    localStorage.setItem(
      SOUND_VOLUME_KEY,
      String(Math.max(0, Math.min(1, volume))),
    );
  } catch {
    // localStorage unavailable
  }
}

export function getSoundType(): SoundType {
  try {
    const stored = localStorage.getItem(SOUND_TYPE_KEY);
    if (stored === "chime" || stored === "ping" || stored === "bell")
      return stored;
  } catch {
    // localStorage unavailable
  }
  return "chime"; // default
}

export function setSoundType(type: SoundType): void {
  try {
    localStorage.setItem(SOUND_TYPE_KEY, type);
  } catch {
    // localStorage unavailable
  }
}

/* ---------- Sound generators ---------- */

/** Two-tone ascending chime (~250ms total). */
function playChime(ctx: AudioContext, volume: number): void {
  const now = ctx.currentTime;

  // Tone 1: 880 Hz (A5) — 100ms
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = "sine";
  osc1.frequency.value = 880;
  gain1.gain.setValueAtTime(0.15 * volume, now);
  gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
  osc1.connect(gain1).connect(ctx.destination);
  osc1.start(now);
  osc1.stop(now + 0.1);

  // Tone 2: 1320 Hz (E6) — 150ms, starts slightly after
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = "sine";
  osc2.frequency.value = 1320;
  gain2.gain.setValueAtTime(0.12 * volume, now + 0.08);
  gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
  osc2.connect(gain2).connect(ctx.destination);
  osc2.start(now + 0.08);
  osc2.stop(now + 0.22);
}

/** Short staccato ping (~150ms). */
function playPing(ctx: AudioContext, volume: number): void {
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = 1046.5; // C6
  gain.gain.setValueAtTime(0.18 * volume, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.15);
}

/** Gentle bell — triangle wave with harmonic decay (~400ms). */
function playBell(ctx: AudioContext, volume: number): void {
  const now = ctx.currentTime;

  // Fundamental: 698 Hz (F5)
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = "triangle";
  osc1.frequency.value = 698;
  gain1.gain.setValueAtTime(0.14 * volume, now);
  gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
  osc1.connect(gain1).connect(ctx.destination);
  osc1.start(now);
  osc1.stop(now + 0.35);

  // Overtone: 1396 Hz (F6) — softer, decays faster
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = "sine";
  osc2.frequency.value = 1396;
  gain2.gain.setValueAtTime(0.06 * volume, now);
  gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
  osc2.connect(gain2).connect(ctx.destination);
  osc2.start(now);
  osc2.stop(now + 0.2);
}

/** Rounded notes with a little warmth and a fade to silence at both edges. */
function playDictationNote(
  ctx: AudioContext,
  frequency: number,
  start: number,
  duration: number,
  volume: number,
  peak = 0.085,
): void {
  for (const [multiple, level] of [
    [1, peak],
    [2, peak / 10],
  ] as const) {
    const tone = ctx.createOscillator();
    const gain = ctx.createGain();
    tone.type = "sine";
    tone.frequency.setValueAtTime(frequency * multiple, start);
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(level * volume, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.00001, start + duration - 0.015);
    gain.gain.linearRampToValueAtTime(0, start + duration);
    tone.connect(gain).connect(ctx.destination);
    tone.onended = () => {
      tone.disconnect();
      gain.disconnect();
    };
    tone.start(start);
    tone.stop(start + duration);
  }
}

/** A soft, rising major third says the microphone is ready (~230ms). */
function playWake(ctx: AudioContext, volume: number): void {
  const now = ctx.currentTime;
  playDictationNote(ctx, 523.25, now, 0.14, volume);
  playDictationNote(ctx, 659.25, now + 0.07, 0.16, volume);
}

/** A low, soft falling fifth says Denker answered in text only (~260ms). */
function playQuietDone(ctx: AudioContext, volume: number): void {
  const now = ctx.currentTime;
  playDictationNote(ctx, 587.33, now, 0.13, volume);
  playDictationNote(ctx, 392, now + 0.09, 0.17, volume);
}

/** The same notes settle downward when listening ends (~205ms). */
function playWindDown(ctx: AudioContext, volume: number): void {
  const now = ctx.currentTime;
  playDictationNote(ctx, 659.25, now, 0.12, volume);
  playDictationNote(ctx, 523.25, now + 0.065, 0.14, volume);
}

/** Live mode's start hum holds for at least this long before it resolves. */
const LIVE_START_MIN_HOLD_S = 0.45;
/** A start hum that never resolves fades out after this long. */
const LIVE_START_MAX_HOLD_MS = 8000;

type LiveHum = {
  startedAt: number;
  fadeOut: (at: number, fade: number) => void;
};

/** A quiet warm hum (G3 + D4) through a soft low-pass that opens or closes
 *  over `sweep` seconds while its pitch glides by `glide`. */
function startLiveHum(
  ctx: AudioContext,
  volume: number,
  shape: {
    from: number;
    to: number;
    sweep: number;
    glide: number;
    attack: number;
  },
): LiveHum {
  const now = ctx.currentTime;
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.Q.setValueAtTime(0.7, now);
  filter.frequency.setValueAtTime(shape.from, now);
  filter.frequency.exponentialRampToValueAtTime(shape.to, now + shape.sweep);
  const out = ctx.createGain();
  // Silent at rest too: a fade that cancels the attack falls back to it.
  out.gain.value = 0;
  out.gain.setValueAtTime(0, now);
  out.gain.linearRampToValueAtTime(0.045 * volume, now + shape.attack);
  filter.connect(out).connect(ctx.destination);
  const tones = [196, 293.66].map((frequency) => {
    const tone = ctx.createOscillator();
    tone.type = "sine";
    tone.frequency.setValueAtTime(frequency, now);
    tone.frequency.exponentialRampToValueAtTime(
      frequency * shape.glide,
      now + shape.sweep,
    );
    tone.connect(filter);
    tone.start(now);
    return tone;
  });
  let fading = false;
  return {
    startedAt: now,
    fadeOut: (at, fade) => {
      if (fading) return;
      fading = true;
      out.gain.cancelScheduledValues(at);
      out.gain.setTargetAtTime(0, at, fade / 3);
      for (const tone of tones) tone.stop(at + fade * 2);
      tones[0]!.onended = () => {
        for (const node of [...tones, filter, out]) node.disconnect();
      };
    },
  };
}

/** Live mode is asking for the microphone: the hum opens up. */
function startLiveHold(ctx: AudioContext, volume: number): LiveHum {
  return startLiveHum(ctx, volume, {
    from: 400,
    to: 900,
    sweep: 0.8,
    glide: 1,
    attack: 0.25,
  });
}

/** Live mode is on: one soft G4 over the fading hum. */
function playLiveOn(ctx: AudioContext, volume: number, at: number): void {
  playDictationNote(ctx, 392, at, 0.51, volume, 0.05);
}

/** Live mode is off: a short hum that closes and sinks (~0.5 s). */
function playLiveOff(ctx: AudioContext, volume: number): void {
  startLiveHum(ctx, volume, {
    from: 900,
    to: 300,
    sweep: 0.4,
    glide: 0.8,
    attack: 0.02,
  }).fadeOut(ctx.currentTime + 0.03, 0.4);
}

/** Every voice capture cue plays at the same level. */
function dictationCueVolume(): number {
  return Math.min(0.42, getSoundVolume() * 0.5);
}

function isOnboardingActive(): boolean {
  const state = useOnboardingStore.getState();
  return (
    state.step !== null &&
    state.step !== "completed" &&
    state.step !== "skipped"
  );
}

function isAgentVoiceEnabled(): boolean {
  const settings = useTtsSettingsStore.getState();
  return settings.enabled && settings.premiumAvailable;
}

/**
 * Task-complete notifications often arrive at the same time as the agent's
 * spoken reply. When Denker is already speaking for the user, keep the
 * completion sound quiet so it does not mask or compete with the reply.
 */
export function shouldPlayNotificationSound(
  notification?: Pick<Notification, "notification_type">,
): boolean {
  if (notification?.notification_type !== "task_completed") return true;
  return !isOnboardingActive() && !isAgentVoiceEnabled();
}

/**
 * Play a specific sound type at a given volume.
 * Used by the preview buttons in settings.
 */
export function playSound(type: SoundType, volume?: number): void {
  try {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") {
      void ctx.resume();
    }
    const vol = volume ?? getSoundVolume();
    switch (type) {
      case "chime":
        playChime(ctx, vol);
        break;
      case "ping":
        playPing(ctx, vol);
        break;
      case "bell":
        playBell(ctx, vol);
        break;
    }
  } catch {
    // Audio not available — silently skip
  }
}

/** Runs a voice capture cue at the cue level, unless sound is off. */
function playVoiceCue(play: (ctx: AudioContext, volume: number) => void): void {
  if (!isSoundEnabled()) return;
  const volume = dictationCueVolume();
  if (volume === 0) return;
  try {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") {
      void ctx.resume();
    }
    play(ctx, volume);
  } catch {
    // Audio not available — silently skip
  }
}

/** Play the short Denker wake cue when voice capture starts. */
export function playDictationWakeSound(): void {
  playVoiceCue(playWake);
}

/** Play the short Denker wind-down cue when voice capture ends. */
export function playDictationWindDownSound(): void {
  playVoiceCue(playWindDown);
}

/** Play live mode's off cue when its capture ends. */
export function playLiveVoiceOffSound(): void {
  playVoiceCue(playLiveOff);
}

/** Settles a live start hum: `true` once the microphone really listens
 *  (the hum resolves into the on note), `false` when the start fails or is
 *  stopped (it fades out). Only the first call counts. */
export type LiveVoiceStartSound = { settle: (on: boolean) => void };

/** Start live mode's hum. It keeps going until it is settled, for at least
 *  `LIVE_START_MIN_HOLD_S`. After `LIVE_START_MAX_HOLD_MS` it fades by
 *  itself, and a late `settle(true)` still plays the on note. */
export function startLiveVoiceStartSound(): LiveVoiceStartSound {
  let settle: LiveVoiceStartSound["settle"] = () => {};
  playVoiceCue((ctx, volume) => {
    const hold = startLiveHold(ctx, volume);
    let settled = false;
    const timer = setTimeout(
      () => hold.fadeOut(ctx.currentTime, 0.3),
      LIVE_START_MAX_HOLD_MS,
    );
    settle = (on) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      const at = on
        ? Math.max(ctx.currentTime, hold.startedAt + LIVE_START_MIN_HOLD_S)
        : ctx.currentTime;
      hold.fadeOut(at, 0.06);
      if (on) playLiveOn(ctx, volume, at);
    };
  });
  return { settle: (on) => settle(on) };
}

/**
 * Play the soft cue that stands for a reply Denker shows without reading it
 * aloud (`desktop/tts-quiet-turns.ts`): quieter than the voice capture cues,
 * under music or a call.
 */
export function playQuietTurnCue(): void {
  if (!isSoundEnabled()) return;
  const volume = dictationCueVolume() * 0.6;
  if (volume === 0) return;
  try {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") {
      void ctx.resume();
    }
    playQuietDone(ctx, volume);
  } catch {
    // Audio not available — silently skip
  }
}

/**
 * Play the user's selected notification sound at their chosen volume.
 */
export function playNotificationSound(
  notification?: Pick<Notification, "notification_type">,
): void {
  if (!isSoundEnabled()) return;
  if (!shouldPlayNotificationSound(notification)) return;
  playSound(getSoundType(), getSoundVolume());
}
